import React, { useState, useEffect } from 'react'
import { Button, Input, Space, Tag, Tooltip, Typography, Card, Modal, Spin } from 'antd'
import TextArea from 'antd/es/input/TextArea'
import styled, { keyframes, css } from 'styled-components'
import {
  IconUsers,
  IconBuildingBank,
  IconPhone,
  IconMessageCircle,
  IconEdit,
  IconCheck,
  IconX,
  IconStar,
  IconStarFilled,
  IconDeviceFloppy,
  IconTrash,
  IconMessage,
  IconSettings,
  IconExclamationCircle,
  IconChevronDown,
  IconChevronRight,
  IconChevronUp
} from '@tabler/icons-react'
import DeleteGroup from '../DeleteGroup'
import SaveGroup from '../SaveGroup'
import SendMessage from '../SendMessage'
import ParentPartner from '../ParentPartner'
import { useToken } from 'app/hooks'
import API from '../../../utils/api'
import { message } from 'antd'
import { useTradeModalStore } from 'app/hooks/trade/modalStore'
import { useExchangeStore } from 'app/hooks/trade/exchangeStore'
import GSlipage from '../GSlipage'

const { Text } = Typography

// 애니메이션 정의
const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`

const LoadingOverlay = styled.div<{ $show: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(2px);
  display: ${props => props.$show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 100;
  border-radius: 8px;

  .ant-spin {
    .ant-spin-dot {
      i {
        background-color: #1890ff;
      }
    }
  }
`

const StyledCard = styled(Card)<{ $isSubgroup?: boolean; $hasSubgroups?: boolean; $isDragOver?: boolean; $isDragging?: boolean; $isUpdating?: boolean; $isCompact?: boolean }>`
  margin: 8px 0;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid ${props => {
    if (props.$isDragOver) return '#1890ff';
    return props.$isSubgroup ? '#ffa940' : '#e9ecef';
  }};
  box-shadow: ${props => {
    if (props.$isDragOver) return '0 4px 12px rgba(24, 144, 255, 0.3)';
    if (props.$isDragging) return '0 8px 16px rgba(0, 0, 0, 0.2)';
    return '0 1px 4px rgba(0, 0, 0, 0.06)';
  }};
  transition: all 0.2s ease;
  overflow: hidden;
  height: fit-content;
  align-self: start;
  opacity: ${props => {
    if (props.$isUpdating) return 0.7;
    if (props.$isDragging) return 0.5;
    return 1;
  }};
  transform: ${props => {
    if (props.$isDragOver) return 'translateY(-2px) scale(1.02)';
    if (props.$isDragging) return 'rotate(2deg)';
    return 'none';
  }};
  pointer-events: ${props => props.$isUpdating ? 'none' : 'auto'};

  &:hover {
    transform: ${props => {
      if (props.$isDragOver) return 'translateY(-2px) scale(1.02)';
      if (props.$isDragging) return 'rotate(2deg)';
      return 'translateY(-1px)';
    }};
    box-shadow: ${props => {
      if (props.$isDragOver) return '0 4px 12px rgba(24, 144, 255, 0.3)';
      if (props.$isDragging) return '0 8px 16px rgba(0, 0, 0, 0.2)';
      return '0 2px 8px rgba(0, 0, 0, 0.1)';
    }};
    border-color: ${props => {
      if (props.$isDragOver) return '#1890ff';
      return props.$isSubgroup ? '#ff7a00' : '#d1ecf1';
    }};
  }

  ${props => props.$isDragOver && `
    &::before {
      content: '';
      position: absolute;
      top: -2px;
      left: -2px;
      right: -2px;
      bottom: -2px;
      background: linear-gradient(45deg, #1890ff, #40a9ff);
      border-radius: 10px;
      z-index: -1;
      animation: dropGlow 1s ease-in-out infinite alternate;
    }

    @keyframes dropGlow {
      from { opacity: 0.3; }
      to { opacity: 0.6; }
    }
  `}

  .ant-card-head {
    background: ${props => {
      if (props.$isSubgroup) {
        return 'linear-gradient(135deg, #ffa940 0%, #ff7a00 100%)'
      } else if (props.$hasSubgroups) {
        return 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)'
      } else {
        return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }
    }};
    border-radius: 8px 8px 0 0;
    border-bottom: none;
    min-height: 32px;
    padding: 0 8px;

    .ant-card-head-title {
      color: white;
      font-weight: 600;
      font-size: 13px;
      padding: 4px 0;
      line-height: 1.2;
    }
  }

  .ant-card-body {
    padding: ${props => props.$isCompact ? '0' : '12px'};
    transition: all 0.3s ease;
  }
`

const ExpandableContent = styled.div<{ $isExpanded: boolean }>`
  max-height: ${props => props.$isExpanded ? '2000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  padding: ${props => props.$isExpanded ? '12px' : '0'};
  padding-top: ${props => props.$isExpanded ? '0' : '0'};
`

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0;
`

const GroupBadge = styled(Tag)`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 4px;
`

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 8px;
`

const InfoSection = styled.div`
  background: #f8f9fa;
  border-radius: 6px;
  padding: 8px;
  border: 1px solid #e9ecef;
`

const SectionTitle = styled.div`
  color: #6c757d;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 6px;
`

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const StatIcon = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 4px;
  background: rgba(102, 126, 234, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    color: #667eea;
  }
`

const StatValue = styled.span`
  font-weight: 600;
  color: #2c3e50;
  font-size: 12px;
`

const ContactInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  margin-top: 4px;
`

const ContactButton = styled(Button)`
  font-size: 9px;
  height: 20px;
  padding: 0 6px;
  border-radius: 3px;

  &.cs1 {
    background: #52c41a;
    border-color: #52c41a;
    color: white;
  }

  &.cs2 {
    background: #1890ff;
    border-color: #1890ff;
    color: white;
  }

  &.cs3 {
    background: #722ed1;
    border-color: #722ed1;
    color: white;
  }

  &.cs4 {
    background: #fa8c16;
    border-color: #fa8c16;
    color: white;
  }
`

const ActionsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e9ecef;
`

const SubgroupsSection = styled.div`
  margin-top: 16px;
  padding: 16px 12px 8px 12px;
  border-top: 2px solid #f0f0f0;
  background: #2c3e50;
  border-radius: 0 0 6px 6px;
  margin-left: -12px;
  margin-right: -12px;
  margin-bottom: -12px;
`

const SubgroupsTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const SubgroupsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`


const CompactButton = styled(Button)`
  font-size: 9px;
  height: 20px;
  padding: 0 6px;
  border-radius: 3px;
  min-width: 20px;

  &.primary {
    background: #1890ff !important;
    border-color: #1890ff !important;
    color: white !important;

    &:hover {
      background: #40a9ff !important;
      border-color: #40a9ff !important;
      color: white !important;
    }
  }

  &.success {
    background: #52c41a !important;
    border-color: #52c41a !important;
    color: white !important;

    &:hover {
      background: #73d13d !important;
      border-color: #73d13d !important;
      color: white !important;
    }
  }

  &.danger {
    background: #ff4d4f !important;
    border-color: #ff4d4f !important;
    color: white !important;

    &:hover {
      background: #ff7875 !important;
      border-color: #ff7875 !important;
      color: white !important;
    }
  }

  &.warning {
    background: #fa8c16 !important;
    border-color: #fa8c16 !important;
    color: white !important;

    &:hover {
      background: #ffa940 !important;
      border-color: #ffa940 !important;
      color: white !important;
    }
  }

  &.pulsing {
    animation: ${pulse} 2s infinite;
  }
`

interface GroupCardProps {
  group: any
  subgroups?: any[]
  onDragStart?: (e: React.DragEvent, group: any) => void
  onDataChange?: () => void
  index?: number
  onReorder?: (dragIndex: number, hoverIndex: number) => void
  onDragOver?: (e: React.DragEvent) => void
  onDrop?: (e: React.DragEvent, group: any) => void
  onDragEnd?: () => void
  isDragging?: boolean
  isDragOver?: boolean
  isUpdating?: boolean
  dragMode?: 'reorder' | 'assign-parent'
  forceExpanded?: boolean
}

const GroupCard: React.FC<GroupCardProps> = ({ group, subgroups, onDragStart, onDataChange, index, onReorder, onDragOver, onDrop, onDragEnd, isDragging, isDragOver, isUpdating, dragMode, forceExpanded }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editedGroup, setEditedGroup] = useState({ ...group })
  const [showSlipageModal, setShowSlipageModal] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [subgroupsExpanded, setSubgroupsExpanded] = useState(false) // 하위그룹 전체 펼치기/접기 상태

  // forceExpanded가 변경되면 isExpanded도 동기화
  useEffect(() => {
    if (forceExpanded !== undefined) {
      setIsExpanded(forceExpanded)
    }
  }, [forceExpanded])
  const { data: token } = useToken()
  const { data: modalStore, mutate: mutateModalStore } = useTradeModalStore()
  const { EPs } = useExchangeStore()

  // EPs 데이터 정렬 (ABC순으로)
  const showEPs = EPs?.filter(ep => ep?.openstatus < 4)?.sort((a, b) => {
    if (a?.pairs < b?.pairs) return -1
    if (a?.pairs > b?.pairs) return 1
    return 0
  })

  // group이 변경될 때 editedGroup 업데이트
  useEffect(() => {
    setEditedGroup({ ...group })
  }, [group])

  // 변경사항 추적
  const hasChanges = React.useMemo(() => {
    return Object.keys(editedGroup).some(key => {
      if (key === 'seq' || key === 'parentid') {
        return editedGroup[key] !== group[key]
      }
      return false
    })
  }, [editedGroup, group])


  const isSubgroup = group?.subgroup === 1 || group?.parentid

  const handleSave = async () => {
    try {
      const postdata = {
        name: editedGroup?.name,
        info_d: editedGroup?.info_d,
        info_w: editedGroup?.info_w,
        cs_1: editedGroup?.cs_1,
        cs_2: editedGroup?.cs_2,
        cs_3: editedGroup?.cs_3,
        cs_4: editedGroup?.cs_4,
        kor_1: editedGroup?.kor_1,
        kor_2: editedGroup?.kor_2,
        kor_3: editedGroup?.kor_3,
        seq: parseInt(editedGroup?.seq) || 0,
        parentid: editedGroup?.parentid
      }
      await API.send('PUT', `/admin/usergroup/${editedGroup?.id}`, token + '', postdata)
      message.success('그룹 정보 저장 완료')
      onDataChange?.()
    } catch (error) {
      message.error('저장 실패')
    }
  }

  const handleDelete = async () => {
    try {
      const res = await API.send('DELETE', `/admin/usergroup/${editedGroup?.id}`, token + '', {}, false)
      if (res.result.success) {
        message.success('그룹 삭제 완료')
        onDataChange?.()
      } else {
        message.error('그룹 삭제 실패')
      }
    } catch (error) {
      message.error('그룹 삭제 실패')
    }
  }

  const handleMessage = () => {
    mutateModalStore({
      ...modalStore,
      sendmodal: true,
      sendtarget: 'group',
      sendtargetid: editedGroup?.id,
    })
  }

  // 디버깅용 로그
  if (subgroups && subgroups.length > 0) {
    console.log(`GroupCard: Parent ${group?.id} (${group?.name}) received ${subgroups.length} subgroups:`, subgroups)
  }

  return (
    <StyledCard
      $isSubgroup={isSubgroup}
      $hasSubgroups={subgroups && subgroups.length > 0}
      $isDragging={isDragging}
      $isDragOver={isDragOver}
      $isUpdating={isUpdating}
      $isCompact={!isExpanded}
      draggable={onDragStart ? true : false}
      onDragStart={(e) => onDragStart?.(e, editedGroup)}
      onDragEnd={() => onDragEnd?.()}
      onDragOver={(e) => {
        e.preventDefault()
        onDragOver?.(e)
      }}
      onDrop={(e) => {
        e.preventDefault()
        onDrop?.(e, editedGroup)
      }}
      onDragEnter={(e) => {
        e.preventDefault()
      }}
      onDragLeave={(e) => {
        e.preventDefault()
      }}
      style={{ cursor: onDragStart ? 'move' : 'not-allowed' }}
      title={
        <HeaderSection>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button
              size="small"
              type="text"
              icon={isExpanded ? <IconChevronDown size={12} /> : <IconChevronRight size={12} />}
              onClick={() => setIsExpanded(!isExpanded)}
              style={{
                color: 'white',
                padding: '2px 4px',
                minWidth: '20px',
                height: '20px',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'
                e.currentTarget.style.transform = 'scale(1.05)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'
                e.currentTarget.style.transform = 'scale(1)'
              }}
              title={isExpanded ? "접기" : "펼치기"}
            />
            <GroupBadge>
              {isSubgroup ? <IconStar size={12} /> : <IconStarFilled size={12} />}
              #{group?.id}
            </GroupBadge>
            {isEditing ? (
              <Input
                size="small"
                style={{ width: 100, fontSize: 11, height: 20 }}
                defaultValue={group?.name}
                onChange={e => {
                  setEditedGroup(prev => ({ ...prev, name: e.target.value }))
                }}
                onPressEnter={() => setIsEditing(false)}
                onBlur={() => setIsEditing(false)}
                autoFocus
              />
            ) : (
              <span
                onClick={() => setIsEditing(true)}
                style={{
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 600,
                  maxWidth: '120px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                title={group?.name}
              >
                {group?.name}
              </span>
            )}
            <span style={{ fontSize: '10px', opacity: 0.9, marginLeft: 4, flexShrink: 0 }}>
              ({group?.usercount || 0}명)
            </span>
            {isSubgroup ? (
              <span style={{ fontSize: '9px', opacity: 0.8, flexShrink: 0 }}>
                하위
              </span>
            ) : (
              <span style={{ fontSize: '9px', opacity: 0.8, flexShrink: 0 }}>
                {subgroups && subgroups.length > 0 ? `상위(${subgroups.length})` : '독립'}
              </span>
            )}
          </div>
          <Button
            size="small"
            type="text"
            icon={<IconEdit size={12} />}
            onClick={() => setIsEditing(!isEditing)}
            style={{
              color: 'white',
              padding: '2px 4px',
              minWidth: '20px',
              height: '20px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '4px'
            }}
            title="그룹명 편집"
          />
        </HeaderSection>
      }
    >
      {/* 접힌 상태에서도 보이는 간단한 정보 */}
      {!isExpanded && (
        <div style={{
          padding: '8px 12px',
          fontSize: '11px',
          color: '#666',
          backgroundColor: '#f8f9fa',
          borderTop: '1px solid #e9ecef'
        }}>
          {subgroups && subgroups.length > 0
            ? `하위그룹 ${subgroups.length}개 • 총 ${subgroups.reduce((sum, sub) => sum + (sub.usercount || 0), 0)}명`
            : `독립그룹 • ${group?.usercount || 0}명`
          }
        </div>
      )}

      <ExpandableContent $isExpanded={isExpanded}>
        {/* 하위그룹이 있는 경우와 없는 경우 다르게 표시 */}
        {subgroups && subgroups.length > 0 ? (
            // 상위그룹 - 하위그룹 정보와 일괄 수정 섹션 표시
            <>
              <InfoGrid>
                <InfoSection>
                  <SectionTitle>하위그룹</SectionTitle>
                  <StatItem>
                    <StatIcon>
                      <IconStar size={12} />
                    </StatIcon>
                    <StatValue>{subgroups.length}개</StatValue>
                  </StatItem>
                </InfoSection>

                <InfoSection>
                  <SectionTitle>총 회원</SectionTitle>
                  <StatItem>
                    <StatIcon>
                      <IconUsers size={12} />
                    </StatIcon>
                    <StatValue>
                      {subgroups.reduce((sum, sub) => sum + (sub.usercount || 0), 0)}명
                    </StatValue>
                  </StatItem>
                </InfoSection>
              </InfoGrid>

              {/* 하위그룹 일괄 수정 섹션 */}
              <div style={{
                marginTop: 12,
                padding: 12,
                backgroundColor: '#e8f4fd',
                borderRadius: 6,
                border: '1px solid #bae7ff'
              }}>
                <SectionTitle style={{ color: '#1890ff', marginBottom: 8 }}>
                  하위그룹 일괄 수정
                </SectionTitle>

                {/* 입출금 안내 섹션 */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 8,
                  marginBottom: 12
                }}>
                  <div>
                    <label style={{ fontSize: 9, color: '#666', marginBottom: 2, display: 'block' }}>
                      입금안내 일괄적용
                    </label>
                    <TextArea
                      size="small"
                      placeholder="하위그룹에 일괄 적용할 입금안내"
                      style={{ fontSize: 9, minHeight: 40 }}
                      autoSize={{ minRows: 2, maxRows: 3 }}
                      value={editedGroup.bulk_info_d || ''}
                      onChange={e => {
                        setEditedGroup(prev => ({ ...prev, bulk_info_d: e.target.value }))
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 9, color: '#666', marginBottom: 2, display: 'block' }}>
                      출금안내 일괄적용
                    </label>
                    <TextArea
                      size="small"
                      placeholder="하위그룹에 일괄 적용할 출금안내"
                      style={{ fontSize: 9, minHeight: 40 }}
                      autoSize={{ minRows: 2, maxRows: 3 }}
                      value={editedGroup.bulk_info_w || ''}
                      onChange={e => {
                        setEditedGroup(prev => ({ ...prev, bulk_info_w: e.target.value }))
                      }}
                    />
                  </div>
                </div>

                {/* 계좌 정보 섹션 */}
                <div style={{
                  marginBottom: 12,
                  padding: 8,
                  backgroundColor: '#f0f8ff',
                  borderRadius: 4,
                  border: '1px solid #d1ecf1'
                }}>
                  <label style={{ fontSize: 9, color: '#666', marginBottom: 6, display: 'block', fontWeight: 600 }}>
                    계좌정보 일괄적용
                  </label>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 2fr 1fr',
                    gap: 6
                  }}>
                    <Input
                      size="small"
                      placeholder="은행명"
                      style={{ fontSize: 9, height: 24 }}
                      value={editedGroup.bulk_kor_1 || ''}
                      onChange={e => {
                        setEditedGroup(prev => ({ ...prev, bulk_kor_1: e.target.value }))
                      }}
                    />
                    <Input
                      size="small"
                      placeholder="계좌번호"
                      style={{ fontSize: 9, height: 24 }}
                      value={editedGroup.bulk_kor_2 || ''}
                      onChange={e => {
                        setEditedGroup(prev => ({ ...prev, bulk_kor_2: e.target.value }))
                      }}
                    />
                    <Input
                      size="small"
                      placeholder="예금주"
                      style={{ fontSize: 9, height: 24 }}
                      value={editedGroup.bulk_kor_3 || ''}
                      onChange={e => {
                        setEditedGroup(prev => ({ ...prev, bulk_kor_3: e.target.value }))
                      }}
                    />
                  </div>
                </div>

                {/* 고객센터 정보 섹션 */}
                <div style={{
                  marginBottom: 12,
                  padding: 8,
                  backgroundColor: '#fff8e1',
                  borderRadius: 4,
                  border: '1px solid #ffe4b3'
                }}>
                  <label style={{ fontSize: 9, color: '#666', marginBottom: 6, display: 'block', fontWeight: 600 }}>
                    고객센터 일괄적용
                  </label>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 6,
                    marginBottom: 4
                  }}>
                    <Input
                      size="small"
                      placeholder="고객센터1"
                      style={{ fontSize: 9, height: 24 }}
                      value={editedGroup.bulk_cs_1 || ''}
                      onChange={e => {
                        setEditedGroup(prev => ({ ...prev, bulk_cs_1: e.target.value }))
                      }}
                    />
                    <Input
                      size="small"
                      placeholder="고객센터2"
                      style={{ fontSize: 9, height: 24 }}
                      value={editedGroup.bulk_cs_2 || ''}
                      onChange={e => {
                        setEditedGroup(prev => ({ ...prev, bulk_cs_2: e.target.value }))
                      }}
                    />
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 6
                  }}>
                    <Input
                      size="small"
                      placeholder="텔레상담링크"
                      style={{ fontSize: 9, height: 24 }}
                      value={editedGroup.bulk_cs_3 || ''}
                      onChange={e => {
                        setEditedGroup(prev => ({ ...prev, bulk_cs_3: e.target.value }))
                      }}
                    />
                    <Input
                      size="small"
                      placeholder="카톡상담링크"
                      style={{ fontSize: 9, height: 24 }}
                      value={editedGroup.bulk_cs_4 || ''}
                      onChange={e => {
                        setEditedGroup(prev => ({ ...prev, bulk_cs_4: e.target.value }))
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                  <CompactButton
                    className="primary"
                    onClick={async () => {
                      const hasInfoData = editedGroup.bulk_info_d || editedGroup.bulk_info_w
                      const hasAccountData = editedGroup.bulk_kor_1 || editedGroup.bulk_kor_2 || editedGroup.bulk_kor_3
                      const hasCsData = editedGroup.bulk_cs_1 || editedGroup.bulk_cs_2 || editedGroup.bulk_cs_3 || editedGroup.bulk_cs_4

                      if (!hasInfoData && !hasAccountData && !hasCsData) {
                        message.warning('일괄 적용할 내용을 입력해주세요')
                        return
                      }

                      try {
                        const updatePromises = subgroups.map(sub => {
                          const updateData: any = {
                            name: sub.name,
                            info_d: editedGroup.bulk_info_d || sub.info_d,
                            info_w: editedGroup.bulk_info_w || sub.info_w,
                            cs_1: editedGroup.bulk_cs_1 || sub.cs_1,
                            cs_2: editedGroup.bulk_cs_2 || sub.cs_2,
                            cs_3: editedGroup.bulk_cs_3 || sub.cs_3,
                            cs_4: editedGroup.bulk_cs_4 || sub.cs_4,
                            kor_1: editedGroup.bulk_kor_1 || sub.kor_1,
                            kor_2: editedGroup.bulk_kor_2 || sub.kor_2,
                            kor_3: editedGroup.bulk_kor_3 || sub.kor_3,
                            seq: sub.seq,
                            parentid: sub.parentid
                          }

                          return API.send('PUT', `/admin/usergroup/${sub.id}`, token + '', updateData)
                        })

                        await Promise.all(updatePromises)
                        message.success(`${subgroups.length}개 하위그룹 일괄 수정 완료`)

                        // 로컬 하위그룹 데이터 즉시 업데이트
                        if (subgroups) {
                          subgroups.forEach(sub => {
                            if (editedGroup.bulk_info_d) sub.info_d = editedGroup.bulk_info_d
                            if (editedGroup.bulk_info_w) sub.info_w = editedGroup.bulk_info_w
                            if (editedGroup.bulk_cs_1) sub.cs_1 = editedGroup.bulk_cs_1
                            if (editedGroup.bulk_cs_2) sub.cs_2 = editedGroup.bulk_cs_2
                            if (editedGroup.bulk_cs_3) sub.cs_3 = editedGroup.bulk_cs_3
                            if (editedGroup.bulk_cs_4) sub.cs_4 = editedGroup.bulk_cs_4
                            if (editedGroup.bulk_kor_1) sub.kor_1 = editedGroup.bulk_kor_1
                            if (editedGroup.bulk_kor_2) sub.kor_2 = editedGroup.bulk_kor_2
                            if (editedGroup.bulk_kor_3) sub.kor_3 = editedGroup.bulk_kor_3
                          })
                        }

                        // 입력 필드 초기화
                        setEditedGroup(prev => ({
                          ...prev,
                          bulk_info_d: '',
                          bulk_info_w: '',
                          bulk_cs_1: '',
                          bulk_cs_2: '',
                          bulk_cs_3: '',
                          bulk_cs_4: '',
                          bulk_kor_1: '',
                          bulk_kor_2: '',
                          bulk_kor_3: ''
                        }))

                        // 화면 갱신을 위한 딜레이 후 데이터 새로고침
                        setTimeout(() => {
                          onDataChange?.()
                        }, 300)
                      } catch (error) {
                        message.error('하위그룹 일괄 수정 실패')
                        console.error('Bulk update failed:', error)
                      }
                    }}
                    style={{ fontSize: 9, height: 24 }}
                  >
                    일괄 적용
                  </CompactButton>
                </div>
              </div>
            </>
          ) : (
            // 독립 그룹 - 계좌/고객센터/입출금안내 정보 표시
            <>
              <InfoGrid>
                <InfoSection>
                  <SectionTitle>계좌</SectionTitle>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Input
                      size="small"
                      placeholder="은행명"
                      style={{ fontSize: 10, height: 20 }}
                      defaultValue={group?.kor_1}
                      onChange={e => {
                        setEditedGroup(prev => ({ ...prev, kor_1: e.target.value }))
                      }}
                    />
                    <Input
                      size="small"
                      placeholder="계좌번호"
                      style={{ fontSize: 9, height: 18 }}
                      defaultValue={group?.kor_2}
                      onChange={e => {
                        setEditedGroup(prev => ({ ...prev, kor_2: e.target.value }))
                      }}
                    />
                    <Input
                      size="small"
                      placeholder="예금주"
                      style={{ fontSize: 9, height: 18 }}
                      defaultValue={group?.kor_3}
                      onChange={e => {
                        setEditedGroup(prev => ({ ...prev, kor_3: e.target.value }))
                      }}
                    />
                  </div>
                </InfoSection>

                <InfoSection>
                  <SectionTitle>고객센터</SectionTitle>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Input
                      size="small"
                      placeholder="고객센터1"
                      style={{ fontSize: 9, height: 18 }}
                      defaultValue={group?.cs_1}
                      onChange={e => {
                        setEditedGroup(prev => ({ ...prev, cs_1: e.target.value }))
                      }}
                    />
                    <Input
                      size="small"
                      placeholder="고객센터2"
                      style={{ fontSize: 9, height: 18 }}
                      defaultValue={group?.cs_2}
                      onChange={e => {
                        setEditedGroup(prev => ({ ...prev, cs_2: e.target.value }))
                      }}
                    />
                    <Input
                      size="small"
                      placeholder="텔레상담링크"
                      style={{ fontSize: 9, height: 18 }}
                      defaultValue={group?.cs_3}
                      onChange={e => {
                        setEditedGroup(prev => ({ ...prev, cs_3: e.target.value }))
                      }}
                    />
                    <Input
                      size="small"
                      placeholder="카톡상담링크"
                      style={{ fontSize: 9, height: 18 }}
                      defaultValue={group?.cs_4}
                      onChange={e => {
                        setEditedGroup(prev => ({ ...prev, cs_4: e.target.value }))
                      }}
                    />
                  </div>
                </InfoSection>
              </InfoGrid>

              {/* 입출금 안내 섹션 추가 */}
              <InfoGrid style={{ marginTop: 8 }}>
                <InfoSection>
                  <SectionTitle>입금안내</SectionTitle>
                  <TextArea
                    size="small"
                    placeholder="입금 안내 메시지를 입력하세요"
                    style={{ fontSize: 9, minHeight: 60 }}
                    defaultValue={group?.info_d}
                    onChange={e => {
                      setEditedGroup(prev => ({ ...prev, info_d: e.target.value }))
                    }}
                    autoSize={{ minRows: 3, maxRows: 5 }}
                  />
                </InfoSection>

                <InfoSection>
                  <SectionTitle>출금안내</SectionTitle>
                  <TextArea
                    size="small"
                    placeholder="출금 안내 메시지를 입력하세요"
                    style={{ fontSize: 9, minHeight: 60 }}
                    defaultValue={group?.info_w}
                    onChange={e => {
                      setEditedGroup(prev => ({ ...prev, info_w: e.target.value }))
                    }}
                    autoSize={{ minRows: 3, maxRows: 5 }}
                  />
                </InfoSection>
              </InfoGrid>
            </>
          )}

          <ActionsSection>
            {/* 첫 번째 줄: 순서, 파트너목록, 저장 */}
            <div style={{
              display: 'flex',
              alignItems: 'stretch',
              justifyContent: 'space-between',
              width: '100%',
              height: '28px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flex: 1
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  whiteSpace: 'nowrap',
                  height: '28px'
                }}>
                  <span style={{
                    fontSize: 10,
                    color: '#6c757d',
                    minWidth: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%'
                  }}>순서</span>
                  <Input
                    size="small"
                    style={{
                      width: 40,
                      height: 28,
                      fontSize: 10,
                      textAlign: 'center',
                      borderColor: editedGroup?.seq !== group?.seq ? '#ff4d4f' : undefined,
                      backgroundColor: editedGroup?.seq !== group?.seq ? '#fff1f0' : undefined
                    }}
                    value={editedGroup?.seq || 0}
                    onChange={e => {
                      setEditedGroup(prev => ({ ...prev, seq: e.target.value }))
                    }}
                  />
                  {editedGroup?.seq !== group?.seq && (
                    <Tooltip title="변경사항이 있습니다. 저장 버튼을 눌러주세요.">
                      <IconExclamationCircle size={12} style={{ color: '#ff4d4f' }} />
                    </Tooltip>
                  )}
                </div>

                {/* 하위그룹이 있는 상위그룹은 상위파트너 선택 불가 */}
                {(!subgroups || subgroups.length === 0) && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    flex: 1,
                    height: '28px'
                  }}>
                    <span style={{
                      fontSize: 10,
                      color: '#6c757d',
                      minWidth: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      height: '100%',
                      whiteSpace: 'nowrap'
                    }}>상위</span>
                    <div style={{ height: '28px', display: 'flex', alignItems: 'center', width: '100%' }}>
                      <ParentPartner data={editedGroup} />
                    </div>
                    {editedGroup?.parentid !== group?.parentid && (
                      <Tooltip title="변경사항이 있습니다. 저장 버튼을 눌러주세요.">
                        <IconExclamationCircle size={12} style={{ color: '#ff4d4f' }} />
                      </Tooltip>
                    )}
                  </div>
                )}
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                height: '28px'
              }}>
                <Tooltip title={hasChanges ? "변경사항이 있습니다. 저장해주세요!" : "저장"}>
                  <CompactButton
                    className={`success ${hasChanges ? 'pulsing' : ''}`}
                    icon={<IconDeviceFloppy size={12} />}
                    onClick={handleSave}
                    style={{
                      boxShadow: hasChanges ? '0 0 10px rgba(82, 196, 26, 0.5)' : undefined,
                      minWidth: '50px',
                      height: '28px'
                    }}
                  >
                    저장
                  </CompactButton>
                </Tooltip>
              </div>
            </div>

            {/* 두 번째 줄: 슬리피지, 메시지, 삭제 */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 6,
              height: '32px',
              backgroundColor: '#f8f9fa',
              borderTop: '1px solid #e9ecef',
              borderRadius: '0 0 6px 6px',
              padding: '6px 12px',
              marginLeft: '-12px',
              marginRight: '-12px',
              marginBottom: '-12px',
              marginTop: '4px'
            }}>
              <Tooltip title="슬리피지 설정">
                <CompactButton
                  className="warning"
                  icon={<IconSettings size={10} />}
                  onClick={() => setShowSlipageModal(true)}
                />
              </Tooltip>
              <Tooltip title="메시지">
                <CompactButton
                  className="primary"
                  icon={<IconMessage size={10} />}
                  onClick={handleMessage}
                />
              </Tooltip>
              <Tooltip title="삭제">
                <CompactButton
                  className="danger"
                  icon={<IconTrash size={10} />}
                  onClick={handleDelete}
                />
              </Tooltip>
            </div>
          </ActionsSection>

          {/* 하위그룹이 있는 경우 표시 */}
          {subgroups && subgroups.length > 0 && (
            <SubgroupsSection>
              <SubgroupsTitle>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <IconStar size={14} />
                  하위그룹 {subgroups.length}개
                </div>
                <CompactButton
                  className="primary"
                  onClick={() => setSubgroupsExpanded(!subgroupsExpanded)}
                  style={{ fontSize: 9, height: 22, padding: '0 8px' }}
                >
                  {subgroupsExpanded ? <IconChevronUp size={10} /> : <IconChevronDown size={10} />}
                  <span style={{ marginLeft: 4 }}>
                    {subgroupsExpanded ? '전체 접기' : '전체 펼치기'}
                  </span>
                </CompactButton>
              </SubgroupsTitle>
              <SubgroupsGrid>
                {subgroups.map((subgroup: any) => (
                  <div key={subgroup.id} style={{ width: '100%' }}>
                    <GroupCard
                      group={subgroup}
                      onDragStart={onDragStart}
                      onDataChange={onDataChange}
                      forceExpanded={subgroupsExpanded}
                    />
                  </div>
                ))}
              </SubgroupsGrid>
            </SubgroupsSection>
          )}
      </ExpandableContent>

      {/* 슬리피지 설정 모달 */}
      <Modal
        title={`그룹별 설정 / 노출, 슬리피지 : ${group?.id}번그룹`}
        open={showSlipageModal}
        width={700}
        onCancel={() => setShowSlipageModal(false)}
        footer={null}
      >
        {showEPs?.map((ep: any, i: number) => (
          <div
            key={ep?.id}
            style={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              borderBottom: '1px solid #cdcdcd',
            }}
          >
            <div
              style={{
                width: '100%',
                padding: 10,
                fontSize: 11,
                fontWeight: 'bold',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                textAlign: 'left',
              }}
            >
              [{ep?.groupname} - {ep?.id}] {ep?.pairs}
            </div>
            <GSlipage group_id={group?.id} ep_id={ep?.id} />
          </div>
        ))}
      </Modal>

      {/* 로딩 오버레이 */}
      <LoadingOverlay $show={isUpdating || false}>
        <Spin size="large" tip={dragMode === 'assign-parent' ? '상위그룹 지정 중...' : '순서 변경 중...'} />
      </LoadingOverlay>
    </StyledCard>
  )
}

export default GroupCard